import { logger } from "@hopper-b2b/datadog";
import {
  NUPAY_AUTH_CODE_PARAM,
  NUPAY_ERROR_PARAM,
  NUPAY_STATE_PARAM,
  type StoredNupayCheckoutContext,
  consumeStoredCheckoutContextFromStorage,
  nupayStateToSearchParams,
  parseNupayState,
  peekStoredCheckoutContextFromStorage,
} from "../checkout/states/payments/api/checkAuthStatus";
import {
  buildCommerceNupayOAuthRedirectUrl,
  isSafeCommerceReturnPath,
} from "./nupayCommerceReturnContext";
import {
  buildLegacyNupayOAuthRedirectUrl,
  hasFlightLegacyCheckoutHints,
  type LegacyNupayOAuthReturnContext,
} from "./nupayLegacyReturnContext";
import { PATH_FLIGHTS_BOOK, PATH_HOME } from "./urlPaths";

export type ResolveNupayOAuthCallbackRedirectUrlArgs = {
  pathname: string;
  search: string;
};

type NupayOAuthCallbackParams = {
  stateParam: string;
  codeParam: string | null;
  errorParam: string | null;
};

export function parseNupayOAuthCallbackSearch(
  search: string
): NupayOAuthCallbackParams | null {
  const params = new URLSearchParams(search);
  const stateParam = params.get(NUPAY_STATE_PARAM);
  const codeParam = params.get(NUPAY_AUTH_CODE_PARAM);
  const errorParam = params.get(NUPAY_ERROR_PARAM);
  if (!stateParam || (!codeParam && !errorParam)) {
    return null;
  }
  return { stateParam, codeParam, errorParam };
}

/** Log-safe metadata for OAuth redirect targets (never includes auth codes). */
export function getNupayOAuthRedirectLogMetadata(
  redirectUrl: string,
  oauth: NupayOAuthCallbackParams
): {
  returnPath: string;
  hasAuthCode: boolean;
  hasOAuthError: boolean;
} {
  try {
    const parsed = new URL(redirectUrl, "https://nubank.local");
    return {
      returnPath: parsed.pathname,
      hasAuthCode: !!oauth.codeParam,
      hasOAuthError: !!oauth.errorParam,
    };
  } catch {
    return {
      returnPath: redirectUrl.split("?")[0] ?? redirectUrl,
      hasAuthCode: !!oauth.codeParam,
      hasOAuthError: !!oauth.errorParam,
    };
  }
}

function withLegacyStateSearchFallback(
  context: LegacyNupayOAuthReturnContext
): LegacyNupayOAuthReturnContext {
  if (context.returnSearch?.trim() || context.stateSearchFallback) {
    return context;
  }
  const stateSearchFallback = nupayStateToSearchParams(context.state ?? {});
  return stateSearchFallback ? { ...context, stateSearchFallback } : context;
}

function resolveLegacyOAuthRedirectUrl(
  context: LegacyNupayOAuthReturnContext,
  oauth: NupayOAuthCallbackParams
): string | null {
  return buildLegacyNupayOAuthRedirectUrl(
    withLegacyStateSearchFallback(context),
    {
      stateParam: oauth.stateParam,
      code: oauth.codeParam,
      error: oauth.errorParam,
    }
  );
}

function resolveNupayOAuthCallbackRedirectFromStoredContext(
  oauth: NupayOAuthCallbackParams,
  storedContext: StoredNupayCheckoutContext | null
): string | null {
  const { stateParam, codeParam, errorParam } = oauth;

  if (
    storedContext?.platform === "commerce" &&
    isSafeCommerceReturnPath(storedContext.returnPath)
  ) {
    return buildCommerceNupayOAuthRedirectUrl(storedContext, {
      code: codeParam,
      error: errorParam,
    });
  }

  if (storedContext?.platform === "legacy") {
    const legacyRedirectUrl = resolveLegacyOAuthRedirectUrl(
      storedContext,
      oauth
    );
    if (legacyRedirectUrl) {
      return legacyRedirectUrl;
    }

    logger()?.warn(
      "NuPay OAuth callback: cannot restore legacy checkout from stored context",
      {
        returnPath: storedContext.returnPath,
        hasReturnSearch: !!storedContext.returnSearch,
      }
    );
    return null;
  }

  const nupayState = parseNupayState(stateParam);
  if (!nupayState || !hasFlightLegacyCheckoutHints(nupayState)) {
    return null;
  }

  const restoredParams = new URLSearchParams(
    nupayStateToSearchParams(nupayState)
  );
  restoredParams.set(NUPAY_STATE_PARAM, stateParam);
  if (codeParam) restoredParams.set(NUPAY_AUTH_CODE_PARAM, codeParam);
  if (errorParam) restoredParams.set(NUPAY_ERROR_PARAM, errorParam);

  return `/${PATH_FLIGHTS_BOOK}?${restoredParams.toString()}`;
}

/**
 * Resolves the post-OAuth redirect target when NuPay returns to b2bportal `/`.
 * Returns null when the URL is not an OAuth callback or context cannot be restored.
 *
 * Side effect: consumes one-time checkout context from session/local storage.
 */
export function resolveNupayOAuthCallbackRedirectUrl({
  pathname,
  search,
}: ResolveNupayOAuthCallbackRedirectUrlArgs): string | null {
  if (pathname !== PATH_HOME) {
    return null;
  }

  const oauth = parseNupayOAuthCallbackSearch(search);
  if (!oauth) {
    return null;
  }

  const storedContext = consumeStoredCheckoutContextFromStorage();
  return resolveNupayOAuthCallbackRedirectFromStoredContext(
    oauth,
    storedContext
  );
}

type PreMountLocation = {
  pathname: string;
  search: string;
  replace: (url: string) => void;
};

export function redirectNupayOAuthCallbackBeforeMount(
  location: PreMountLocation = window.location
): boolean {
  const redirectUrl = resolveNupayOAuthCallbackRedirectUrl({
    pathname: location.pathname,
    search: location.search,
  });
  if (!redirectUrl) {
    return false;
  }

  const oauth = parseNupayOAuthCallbackSearch(location.search);
  logger()?.info("NuPay OAuth callback: pre-mount redirect to checkout", {
    ...(oauth
      ? getNupayOAuthRedirectLogMetadata(redirectUrl, oauth)
      : { returnPath: redirectUrl.split("?")[0] }),
  });
  location.replace(redirectUrl);
  return true;
}

/**
 * NuPay OAuth callbacks can land on `/auth/startsession/` with `?state=&code=`.
 * Marketplace session auth only sends `?code=` (no state). When legacy checkout
 * context is pending, route to `/hotels/book/` or `/flights/book/` instead of
 * calling startSession with the NuPay authorization code.
 *
 * Side effect: consumes checkout context when a redirect URL is returned.
 */
export function resolveMisroutedLegacyNupayOAuthRedirect(
  search: string
): string | null {
  const oauth = parseNupayOAuthCallbackSearch(search);
  if (!oauth) {
    return null;
  }

  const storedContext = peekStoredCheckoutContextFromStorage();
  // Peek before consume: commerce context lives in the same storage key and
  // must not be removed when this path handles a legacy-only misroute check.
  if (storedContext?.platform !== "legacy") {
    return null;
  }

  const legacyRedirectUrl = resolveLegacyOAuthRedirectUrl(storedContext, oauth);

  if (!legacyRedirectUrl) {
    logger()?.warn(
      "NuPay OAuth callback: cannot restore legacy checkout at startSession",
      {
        returnPath: storedContext.returnPath,
        hasReturnSearch: !!storedContext.returnSearch,
      }
    );
    return null;
  }

  consumeStoredCheckoutContextFromStorage();
  logger()?.info(
    "NuPay OAuth callback: redirecting misrouted legacy checkout from startSession",
    getNupayOAuthRedirectLogMetadata(legacyRedirectUrl, oauth)
  );
  return legacyRedirectUrl;
}
